import { supabase } from "../../supabaseClient";

const VALID_STATUSES = new Set([
  "open",
  "acknowledged",
  "resolved",
  "closed",
]);

const VALID_REQUESTER_TYPES = new Set([
  "partner",
  "learner",
  "admin",
]);

function cleanStatus(value) {
  const status = String(value || "")
    .trim()
    .toLowerCase();

  if (!status) {
    return null;
  }

  if (!VALID_STATUSES.has(status)) {
    throw new Error("Invalid bug report status.");
  }

  return status;
}

function cleanRequesterType(value) {
  const requesterType = String(value || "")
    .trim()
    .toLowerCase();

  if (!requesterType) {
    return null;
  }

  if (!VALID_REQUESTER_TYPES.has(requesterType)) {
    throw new Error("Invalid support requester type.");
  }

  return requesterType;
}

function normaliseReport(row) {
  if (!row) {
    return null;
  }

  return {
    report_id: row.report_id || "",
    requester_user_id:
      row.requester_user_id || "",
    requester_type:
      row.requester_type || "learner",

    requester_name:
      row.requester_name || "Gwin user",
    requester_email:
      row.requester_email || "",

    partner_id:
      row.partner_id || null,
    partner_name:
      row.partner_name || "",

    request_type:
      row.request_type || "bug_report",

    subject:
      row.subject || "",
    message:
      row.message || "",
    page_path:
      row.page_path || "",

    status:
      row.status || "open",

    created_at:
      row.created_at || null,
    updated_at:
      row.updated_at || null,

    acknowledged_at:
      row.acknowledged_at || null,
    resolved_at:
      row.resolved_at || null,
    closed_at:
      row.closed_at || null,
  };
}

function normaliseSummary(row) {
  return {
    open_count:
      Number(row?.open_count) || 0,

    acknowledged_count:
      Number(row?.acknowledged_count) || 0,

    needs_attention_count:
      Number(row?.needs_attention_count) || 0,

    resolved_count:
      Number(row?.resolved_count) || 0,

    closed_count:
      Number(row?.closed_count) || 0,
  };
}

export async function fetchAdminBugReports({
  status = null,
  limit = 200,
  offset = 0,
} = {}) {
  const cleanLimit = Math.max(
    1,
    Math.min(
      500,
      Number(limit) || 200
    )
  );

  const cleanOffset = Math.max(
    0,
    Number(offset) || 0
  );

  const {
    data,
    error,
  } = await supabase.rpc(
    "admin_get_gwin_bug_reports",
    {
      p_status:
        cleanStatus(status),

      p_limit:
        cleanLimit,

      p_offset:
        cleanOffset,
    }
  );

  if (error) {
    throw error;
  }

  return (
    Array.isArray(data)
      ? data
      : []
  )
    .map(normaliseReport)
    .filter(Boolean);
}

export async function fetchAdminSupportRequests({
  requesterType = null,
  status = null,
  limit = 200,
  offset = 0,
} = {}) {
  const cleanLimit = Math.max(
    1,
    Math.min(
      500,
      Number(limit) || 200
    )
  );

  const cleanOffset = Math.max(
    0,
    Number(offset) || 0
  );

  const {
    data,
    error,
  } = await supabase.rpc(
    "admin_get_gwin_support_requests",
    {
      p_requester_type:
        cleanRequesterType(requesterType),

      p_status:
        cleanStatus(status),

      p_limit:
        cleanLimit,

      p_offset:
        cleanOffset,
    }
  );

  if (error) {
    throw error;
  }

  return (
    Array.isArray(data)
      ? data
      : []
  )
    .map(normaliseReport)
    .filter(Boolean);
}

export async function fetchAdminBugReportSummary() {
  const {
    data,
    error,
  } = await supabase
    .rpc(
      "admin_get_gwin_bug_report_summary"
    )
    .maybeSingle();

  if (error) {
    throw error;
  }

  return normaliseSummary(data);
}

export async function fetchAdminSupportRequestSummary(
  requesterType = null
) {
  const {
    data,
    error,
  } = await supabase
    .rpc(
      "admin_get_gwin_support_request_summary",
      {
        p_requester_type:
          cleanRequesterType(requesterType),
      }
    )
    .maybeSingle();

  if (error) {
    throw error;
  }

  return normaliseSummary(data);
}

export async function fetchAdminBugReportEvents(
  reportId
) {
  const cleanReportId =
    String(
      reportId || ""
    ).trim();

  if (!cleanReportId) {
    throw new Error(
      "A bug report is required."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "admin_get_gwin_bug_report_events",
    {
      p_report_id:
        cleanReportId,
    }
  );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data
    : [];
}

export async function updateAdminBugReportStatus({
  reportId,
  status,
  note = "",
}) {
  const cleanReportId =
    String(
      reportId || ""
    ).trim();

  const cleanTargetStatus =
    cleanStatus(status);

  const cleanNote =
    String(
      note || ""
    ).trim();

  if (!cleanReportId) {
    throw new Error(
      "A bug report is required."
    );
  }

  if (
    ![
      "acknowledged",
      "resolved",
      "closed",
    ].includes(
      cleanTargetStatus
    )
  ) {
    throw new Error(
      "Invalid bug report status."
    );
  }

  if (
    cleanNote.length > 1000
  ) {
    throw new Error(
      "The support note may not exceed 1000 characters."
    );
  }

  const {
    data: {
      session,
    },
    error:
      sessionError,
  } =
    await supabase.auth
      .getSession();

  if (sessionError) {
    throw sessionError;
  }

  const accessToken =
    session?.access_token;

  if (!accessToken) {
    throw new Error(
      "Your admin session could not be found. Please log in again."
    );
  }

  const response =
    await fetch(
      "/.netlify/functions/admin-support-status-notification",
      {
        method: "POST",
        headers: {
          "Content-Type":
            "application/json",
          Authorization:
            `Bearer ${accessToken}`,
        },
        body:
          JSON.stringify({
            reportId:
              cleanReportId,
            status:
              cleanTargetStatus,
            note:
              cleanNote || null,
          }),
      }
    );

  const result =
    await response
      .json()
      .catch(
        () => ({})
      );

  if (!response.ok) {
    const error =
      new Error(
        result?.error ||
          "The support request could not be updated."
      );

    error.statusUpdated =
      Boolean(
        result?.status_updated
      );

    error.updated =
      result?.updated || null;

    throw error;
  }

  return {
    ...(result?.updated || {}),
    email_sent:
      Boolean(
        result?.email_sent
      ),
    email_skipped:
      Boolean(
        result?.email_skipped
      ),
    notified_status:
      result?.notified_status ||
      null,
  };
}
